import { BookIcon, HeartIcon } from 'lucide-preact';

import BellNotifications from '@/components/BellNotifications';
import BurgerMenu from '@/components/BurgerMenu';
import { ButtonLink } from '@/components/Buttons';
import { selectHabitatForProductsPromptModal } from '@/components/HabitatLink/utils';
import TalkNavItem from '@/components/Header/TalkNavItem';
import NavItem from '@/components/NavBar/NavItem';
import NavMenuContent from '@/components/NavBar/NavMenuContent';
import NavMenuFooter from '@/components/NavBar/NavMenuFooter';
import useOpenSpeedyFlow from '@/components/SpeedyFlow/hooks/useOpenSpeedyFlow';
import { useSelector, useStore } from '@/redux/helper';
import { selectIsFreeUser, selectShowsEducatorsApp, selectShowsFamilyRoom } from '@/redux/selectors/users';
import { toEducatorsURL } from '@/shared/educatorsApp';

import UserMenu from '../Menu';

const AuthenticatedHeaderContent = ({ isLargeScreen }: { isLargeScreen: boolean }) => {
  const store = useStore();
  const openSpeedyFlow = useOpenSpeedyFlow();
  const showsEducatorsApp = useSelector(selectShowsEducatorsApp);
  const educatorsAppURL = useSelector((state) => state.remoteConfig.educatorsAppURL);
  const isFreeUser = useSelector(selectIsFreeUser);
  const showsFamilyRoom = useSelector(selectShowsFamilyRoom);

  const educatorsAppLink = isLargeScreen
    ? educatorsAppURL
    : (() => {
        if (!educatorsAppURL) {
          return undefined;
        }
        const url = new URL(educatorsAppURL);
        url.pathname = '/about';
        return url.toString();
      })();

  const familyRoomLink = toEducatorsURL(educatorsAppURL, '/educators/family-room');

  return isLargeScreen ? (
    <>
      <nav>
        <ul className="preflight preflight-ul flex select-none items-center gap-6">
          {showsFamilyRoom && familyRoomLink ? (
            <li>
              <ButtonLink
                variant="tertiary"
                size="sm"
                href={familyRoomLink}
                className="min-w-32 bg-sun-200 font-bold text-stone-700 hover:bg-sun-400"
              >
                Family Room
              </ButtonLink>
            </li>
          ) : null}
          {showsEducatorsApp ? (
            <li>
              <NavItem
                avoidInnerPadding
                text="Resources"
                url={educatorsAppLink}
                customIcon={<BookIcon className="size-5 text-stone-500 group-hover:text-stone-900" />}
                data-tour="educator-resources"
              />
            </li>
          ) : null}
          <li>
            <NavItem avoidInnerPadding text="My Animals" url="/habitats" customIcon={<HeartIcon className="size-5" />} />
          </li>
          <li>
            <TalkNavItem avoidInnerPadding />
          </li>
        </ul>
      </nav>
      <div className="ml-9 flex items-center gap-4">
        {isFreeUser ? (
          <div>
            <button
              type="button"
              onClick={() => {
                const habitat = selectHabitatForProductsPromptModal(store.getState());
                openSpeedyFlow({ kind: 'habitat-access', habitatId: habitat?._id, entryPoint: 'header' });
              }}
              className="preflight preflight-button min-w-0 rounded-full bg-primary-green px-4 py-2 text-sm/4 font-medium text-white hover:bg-secondary-green"
            >
              Upgrade
            </button>
          </div>
        ) : null}
        <BellNotifications />
        <UserMenu />
      </div>
    </>
  ) : (
    <div className="ml-4 flex items-center gap-4">
      <BellNotifications />
      <BurgerMenu>
        <div>
          <NavMenuContent />
          <NavMenuFooter className="my-3" />
        </div>
      </BurgerMenu>
    </div>
  );
};

export default AuthenticatedHeaderContent;
